import * as React from "react";
import { motion, LayoutGroup } from "motion/react";
import { cn } from "../lib/cn";
import { Skeleton } from "./Skeleton";
import { Avatar, type AvatarProps } from "./Avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./Tooltip";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * AvatarGroup — stacked overlapping avatars with a `+N` overflow pill,
 * optional per-item tooltip (showing the name), and three opt-in hover
 * effects.
 *
 * Anatomy:
 *
 *   <AvatarGroup
 *     items={[{ name: "Alice", src }, ...]}
 *     maxVisible={4}
 *     size="sm"
 *     hoverEffect="fan"
 *     onItemClick={(item, i) => ...}
 *     onOverflowClick={(hidden) => openMembersDialog(hidden)}
 *   />
 *
 * `hoverEffect` modes:
 *
 *   none — default. A quiet stack. Right for toolbars and table cells.
 *   lift — single avatar lifts on hover (z-index + scale 1.05).
 *   fan  — the whole group spreads out on hover via motion@12 layout
 *          animation. Right for "team" pickers and hero showcases.
 *
 * Honors `prefers-reduced-motion` — the `fan` animation falls back to a
 * snappy CSS-gap transition without the layout interpolation.
 *
 * Self-wraps a `<TooltipProvider>` only if no parent provider exists,
 * making the component drop-in safe even in apps that haven't set one
 * up at the root.
 */

export interface AvatarGroupItem {
  name?: string;
  src?: string;
  fallback?: React.ReactNode;
  /** Optional href — clicking the avatar navigates instead of firing onItemClick. */
  href?: string;
  /** Optional override for the per-item label shown in the tooltip. Defaults to `name`. */
  label?: string;
}

export interface AvatarGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  items: AvatarGroupItem[];
  /** Max avatars to render before overflowing into a `+N` pill. Default 4. */
  maxVisible?: number;
  /**
   * The roster is still loading — renders stacked circle silhouettes at the
   * real diameter, ring and overlap, so the row reserves its exact width.
   * An avatar stack usually sits inline in a header next to a title; letting
   * it pop in from zero width nudges that title sideways.
   */
  loading?: boolean;
  /** Silhouette count while `loading`. Default 3. */
  loadingCount?: number;
  /**
   * Rendered when `items` is empty — e.g. "No members yet". Omitted by
   * default, since a shared surface with no collaborators on it is usually
   * blank rather than annotated.
   */
  empty?: React.ReactNode;
  /** Avatar size — passed through to each Avatar. Default `sm`. */
  size?: AvatarProps["size"];
  /**
   * Avatar shape — forwarded to each inner Avatar, the ring wrapper, and
   * the `+N` overflow pill so consumer apps that have standardized on a
   * non-circular avatar read can render groups consistently. Default
   * `circle` (preserves existing behavior).
   */
  shape?: AvatarProps["shape"];
  /** Overlap density. Default `normal`. */
  spacing?: "tight" | "normal" | "loose";
  /** Hover behavior on the whole group. Default `none`. */
  hoverEffect?: "none" | "lift" | "fan";
  /** Render an inline tooltip over each avatar (uses Tooltip + name). Default true. */
  showTooltip?: boolean;
  /** Click handler per avatar (not invoked for the +N pill). */
  onItemClick?: (item: AvatarGroupItem, index: number) => void;
  /** Click handler for the +N overflow pill. Receives the hidden items. */
  onOverflowClick?: (hidden: AvatarGroupItem[]) => void;
}

/* Spacing — negative margins between siblings so they overlap. The first
 * child gets `first:ml-0` to anchor the row. The fan effect re-uses these
 * by toggling between the negative-margin (stacked) and a positive `gap`
 * (fanned) state. */
const SPACING_STACK: Record<NonNullable<AvatarGroupProps["spacing"]>, string> = {
  tight: "-space-x-2",
  normal: "-space-x-3",
  loose: "-space-x-4",
};

const SPACING_FAN: Record<NonNullable<AvatarGroupProps["spacing"]>, string> = {
  tight: "gap-1",
  normal: "gap-1.5",
  loose: "gap-2",
};

/* Avatar diameter per size — used to size the `+N` overflow pill so it
 * matches the row exactly. Mirrors the avatar variants in Avatar.tsx. */
const SIZE_PX: Record<NonNullable<AvatarGroupProps["size"]>, number> = {
  xs: 24,
  sm: 32,
  default: 40,
  lg: 48,
  xl: 64,
};

const SIZE_FONT: Record<NonNullable<AvatarGroupProps["size"]>, string> = {
  xs: "text-[10px]",
  sm: "text-xs",
  default: "text-sm",
  lg: "text-base",
  xl: "text-lg",
};

/* Mirrors the avatar `shape` cva variant in Avatar.tsx so the ring wrapper
 * and the `+N` pill stay corner-aligned with the inner Avatar. Update both
 * sides if the avatar shape vocabulary ever expands. */
const SHAPE_RADIUS: Record<NonNullable<AvatarGroupProps["shape"]>, string> = {
  circle: "rounded-full",
  rounded: "rounded-2xl",
  square: "rounded-none",
};

/**
 * Renders a tooltip wrapper only when both (a) `showTooltip` is on AND
 * (b) the item has a label/name. Otherwise returns the trigger as-is —
 * we don't want empty tooltip portals cluttering the accessibility tree.
 */
function MaybeTooltip({
  enabled,
  label,
  children,
}: {
  enabled: boolean;
  label?: string;
  children: React.ReactElement;
}): React.ReactElement {
  if (!enabled || !label) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      items,
      maxVisible = 4,
      size = "sm",
      shape = "circle",
      spacing = "normal",
      hoverEffect = "none",
      showTooltip = true,
      loading = false,
      loadingCount = 3,
      empty,
      onItemClick,
      onOverflowClick,
      className,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const [fanned, setFanned] = React.useState(false);

    const visible = items.slice(0, maxVisible);
    const overflow = items.length - maxVisible;
    const hidden = overflow > 0 ? items.slice(maxVisible) : [];

    const isFanMode = hoverEffect === "fan";
    const expanded = isFanMode && fanned;

    // Reduce-motion: skip the layout animation, but still let `fan` toggle
    // the gap so the visual still works — just snappier.
    const useLayout = isFanMode && !reduced;

    const px = SIZE_PX[size ?? "sm"];
    const fontCls = SIZE_FONT[size ?? "sm"];
    const radiusCls = SHAPE_RADIUS[shape ?? "circle"];

    const handleEnter = isFanMode ? () => setFanned(true) : undefined;
    const handleLeave = isFanMode ? () => setFanned(false) : undefined;

    if (loading) {
      return (
        <div
          ref={ref}
          aria-busy="true"
          className={cn("flex items-center", SPACING_STACK[spacing], className)}
          {...props}
        >
          {Array.from({ length: Math.max(1, loadingCount) }).map((_, i) => (
            <Skeleton
              key={i}
              aria-hidden="true"
              // ring-2 ring-background is what separates the real stacked
              // avatars; without it the silhouettes fuse into one blob.
              className={cn("shrink-0 ring-2 ring-background", radiusCls)}
              style={{ width: px, height: px }}
            />
          ))}
        </div>
      );
    }

    if (items.length === 0 && empty) {
      return (
        <div ref={ref} className={className} {...props}>
          {empty}
        </div>
      );
    }

    return (
      <TooltipProvider delayDuration={150}>
        <div
          ref={ref}
          data-fan-state={isFanMode ? (fanned ? "open" : "closed") : undefined}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onFocus={handleEnter}
          onBlur={handleLeave}
          className={cn(
            "flex items-center transition-[gap,margin] duration-200 ease-smooth-out",
            expanded ? SPACING_FAN[spacing] : SPACING_STACK[spacing],
            className,
          )}
          {...props}
        >
          <LayoutGroup>
            {visible.map((item, i) => {
              const tooltipLabel = item.label ?? item.name;
              const lift = hoverEffect === "lift";
              const Wrapper = useLayout ? motion.span : "span";
              const wrapperProps = useLayout ? { layout: true } : {};

              const interactiveProps = item.href
                ? { href: item.href }
                : onItemClick
                  ? {
                      onClick: () => onItemClick(item, i),
                      role: "button" as const,
                      tabIndex: 0,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onItemClick(item, i);
                        }
                      },
                    }
                  : {};

              const Tag = item.href ? "a" : "span";

              const inner = (
                <Tag
                  {...(interactiveProps as Record<string, unknown>)}
                  className={cn(
                    "relative inline-flex ring-2 ring-background",
                    radiusCls,
                    "transition-transform duration-180 ease-smooth-out",
                    lift && "hover:z-10 hover:scale-105 focus-visible:z-10 focus-visible:scale-105",
                    (item.href || onItemClick) &&
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  )}
                  style={{ zIndex: visible.length - i }}
                >
                  <Avatar
                    name={item.name}
                    src={item.src}
                    fallback={item.fallback}
                    size={size}
                    shape={shape}
                    colorFromName={!item.src && !item.fallback}
                  />
                </Tag>
              );

              return (
                <Wrapper
                  key={`${item.src ?? item.name ?? "avatar"}-${i}`}
                  {...wrapperProps}
                  className="inline-flex"
                >
                  <MaybeTooltip enabled={showTooltip} label={tooltipLabel}>
                    {inner}
                  </MaybeTooltip>
                </Wrapper>
              );
            })}

            {overflow > 0 ? (
              <Wrapper isLayout={useLayout}>
                <MaybeTooltip
                  enabled={showTooltip && hidden.length > 0}
                  label={hidden
                    .map((h) => h.label ?? h.name)
                    .filter(Boolean)
                    .join(", ")}
                >
                  <button
                    type="button"
                    onClick={onOverflowClick ? () => onOverflowClick(hidden) : undefined}
                    aria-label={`${overflow} more`}
                    className={cn(
                      "relative inline-flex items-center justify-center",
                      radiusCls,
                      "ring-2 ring-background bg-muted text-muted-foreground font-semibold",
                      fontCls,
                      "transition-transform duration-180 ease-smooth-out",
                      hoverEffect === "lift" &&
                        "hover:z-10 hover:scale-105 focus-visible:z-10 focus-visible:scale-105",
                      onOverflowClick &&
                        "hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer",
                    )}
                    style={{ width: px, height: px, zIndex: 0 }}
                    disabled={!onOverflowClick}
                  >
                    +{overflow}
                  </button>
                </MaybeTooltip>
              </Wrapper>
            ) : null}
          </LayoutGroup>
        </div>
      </TooltipProvider>
    );
  },
);
AvatarGroup.displayName = "AvatarGroup";

/**
 * Tiny indirection so we can pass `layout` only when motion is wanted —
 * keeps the `+N` pill out of the layout group when the user opted out
 * of motion or isn't using `fan`.
 */
function Wrapper({ isLayout, children }: { isLayout: boolean; children: React.ReactNode }) {
  if (isLayout) {
    return (
      <motion.span layout className="inline-flex">
        {children}
      </motion.span>
    );
  }
  return <span className="inline-flex">{children}</span>;
}
